// ── SocialLinksRow ────────────────────────────────────────────────────────────
// Fila de redes sociales con iconos circulares — el patrón de LEADS promovido
// a primitive (lo usan LeadDetailPage y ContactDetail; REGLA #13). Icono en
// cian = hay enlace (click abre en pestaña nueva); en gris = vacío (click
// abre el modal para agregar la URL). El caller decide dónde persistir.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../Icon.js';
import { IconButton } from './IconButton.js';
import { Modal } from './Modal.js';
import { Input } from './Input.js';
import { Button } from './Button.js';

export type SocialNetKey =
  | 'socialFacebook'
  | 'socialInstagram'
  | 'socialTwitter'
  | 'socialLinkedin'
  | 'socialYoutube'
  | 'socialTiktok';

export const SOCIAL_NETS: Array<{ key: SocialNetKey; label: string; icon: IconName }> = [
  { key: 'socialFacebook', label: 'Facebook', icon: 'social-facebook' },
  { key: 'socialInstagram', label: 'Instagram', icon: 'social-instagram' },
  { key: 'socialTwitter', label: 'Twitter', icon: 'social-twitter' },
  { key: 'socialLinkedin', label: 'LinkedIn', icon: 'social-linkedin' },
  { key: 'socialYoutube', label: 'YouTube', icon: 'social-youtube' },
  { key: 'socialTiktok', label: 'TikTok', icon: 'social-tiktok' },
];

interface SocialLinksRowProps {
  values: Partial<Record<SocialNetKey, string | null | undefined>>;
  onSave: (key: SocialNetKey, url: string) => void | Promise<void>;
  saving?: boolean;
}

export function SocialLinksRow({ values, onSave, saving = false }: SocialLinksRowProps) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState<{ key: SocialNetKey; label: string } | null>(null);
  const [urlInput, setUrlInput] = useState('');

  function saveUrl() {
    if (!adding || !urlInput.trim()) return;
    void Promise.resolve(onSave(adding.key, urlInput.trim())).then(() => setAdding(null));
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
        {SOCIAL_NETS.map(({ key, label, icon }) => {
          const url = values[key];
          const isActive = !!url;
          return (
            <IconButton
              key={key}
              icon={<Icon name={icon} size={16} strokeWidth={1.5} />}
              ariaLabel={label}
              tooltip={isActive ? label : t('leads.detail.social.addTitle', { network: label })}
              variant="outline"
              tone={isActive ? 'cyan' : 'muted'}
              size="md"
              shape="circle"
              onClick={() =>
                isActive
                  ? window.open(url!, '_blank', 'noopener,noreferrer')
                  : (setAdding({ key, label }), setUrlInput(''))
              }
            />
          );
        })}
      </div>

      {adding && (
        <Modal
          open
          onClose={() => setAdding(null)}
          size="sm"
          title={t('leads.detail.social.addTitle', { network: adding.label })}
          footer={
            <>
              <Button variant="ghost" tone="muted" size="sm" onClick={() => setAdding(null)}>
                {t('common.cancel')}
              </Button>
              <Button variant="solid" tone="cyan" size="sm" loading={saving} disabled={!urlInput.trim()} onClick={saveUrl}>
                {t('leads.detail.social.addBtn')}
              </Button>
            </>
          }
        >
          <Input
            type="url"
            value={urlInput}
            onChange={setUrlInput}
            onKeyDown={e => { if (e.key === 'Enter') saveUrl(); }}
            placeholder={t('leads.detail.social.addPlaceholder')}
            autoFocus
            fullWidth
          />
        </Modal>
      )}
    </>
  );
}
